import RBush from 'rbush';
import classNames from 'classnames';

import { IRBushItem, TFeaturePropertiesRequiredPrice, TFeature } from '../../../types/map';
import { commarize } from '../../number';
import { FAVORITE_ICON } from './constants';
import { formatBalloonCount } from './utils';
import { getSpecialPromoMarkup } from './markups/specialPromo';
import * as styles from './styles.css';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
}

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;

        public constructor(options: IBalloonManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;

          this.objectManager = new ymaps.ObjectManager();
        }

        public updateBalloons = () => {
          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          this.objectManager.add({
            type: 'FeatureCollection',
            features: rBushItems.map(this.prepareBalloon).filter(feature => !!feature) as TFeature[],
          });
        };

        public updateBalloonProperties = (featureId: string, properties: TFeaturePropertiesRequiredPrice) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (!properties.minPrice && !properties.specialPromo) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: 1,
            },
          };
        };

        private prepareBalloonLayout = (properties: TFeaturePropertiesRequiredPrice) => {
          const content = this.prepareBalloonContent(properties);

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;
              const offsetY = -boundingRect.height - 4;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: TFeaturePropertiesRequiredPrice) {
          const { count, minPrice, favoriteIds, isViewed, hasNewobject, isActive, specialPromo } = feature;

          if (specialPromo) {
            return getSpecialPromoMarkup(feature);
          }

          const price = `${count > 1 ? 'от ' : ''}${commarize(minPrice)}`;
          const countMarkup = count > 1 ? `<div class="${styles['count']}">${formatBalloonCount(count)}</div>` : '';
          const favoriteMarkup =
            favoriteIds && favoriteIds.length > 0 ? `<div class="${styles['favorite']}">${FAVORITE_ICON}</div>` : '';

          return `
            <div class="${classNames(styles['wrapper'], isViewed && styles['viewed'])}">
              <div class="${classNames(
                styles['balloon'],
                hasNewobject && styles['residential-complex'],
                isActive && styles['active'],
              )}">
                ${countMarkup}
                <div class="${styles['price']}">${price}</div>
                ${favoriteMarkup}
              </div>
            </div>
          `;
        }
      }

      provide(BalloonManager);
    },
  );
}
